// packages/app/src/App.tsx
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import apiDocsPluginAlpha from '@backstage/plugin-api-docs/alpha';
import { catalogPlugin } from '@backstage/plugin-catalog';
import catalogPluginAlpha from '@backstage/plugin-catalog/alpha';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import scaffolderPluginAlpha from '@backstage/plugin-scaffolder/alpha';
import { orgPlugin } from '@backstage/plugin-org';
import orgPluginAlpha from '@backstage/plugin-org/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import techdocsPluginAlpha from '@backstage/plugin-techdocs/alpha';
// Addons do TechDocs reader (ReportIssue/TextSize/LightBox/ExpandableNavigation)
// agora vêm como módulos /alpha, substituindo os <TechDocsAddons> do compat bridge.
import {
  techDocsExpandableNavigationAddonModule,
  techDocsLightBoxAddonModule,
  techDocsReportIssueAddonModule,
  techDocsTextSizeAddonModule,
} from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import { appModuleHostCustomizations, hostPlugin } from './hostPlugin';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyRouteRefs,
  convertLegacyPlugin,
} from '@backstage/core-compat-api';
// catalogGraphPlugin não é importado/listado: vem via auto-discovery
// (app.packages no app-config). É o primeiro plugin migrado para discovery —
// os demais do features[] seguem o mesmo caminho incrementalmente.
import catalogUnprocessedEntitiesPlugin from '@backstage/plugin-catalog-unprocessed-entities/alpha';
import devtoolsPlugin from '@backstage/plugin-devtools/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import { devtoolsCustomizationPlugin } from './components/devtools/devtoolsCustomizationPlugin';
import ciandtspikeSecretsPlugin from '@ciandtspike.io/plugin-ciandtspike-frontend-multicloud-secrets';
import ciandtspikeDeleteEntityPlugin, {
  ciandtspikeDeleteEntityTranslationsModule,
} from '@ciandtspike.io/plugin-ciandtspike-frontend-delete-entity';
import grafanaObservabilityPlugin from '@internal/backstage-plugin-grafana-observability-frontend';
import crossplaneResourcesPlugin from '@terasky/backstage-plugin-crossplane-resources-frontend';
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha';
// home/alpha entra pela visitsApi (homepage.visits) e pelo VisitListener que o
// VisitListener/widgets da Home consomem. Sua page nasce em `/home` (não colide
// com a Home customizada em `/`, registrada no hostPlugin).
import homePlugin from '@backstage/plugin-home/alpha';
import {
  ciandtspikeMarketplacePlugin,
  ciandtspikeMarketplaceTranslationsModule,
} from '@ciandtspike.io/plugin-ciandtspike-marketplace';
import { ciandtspikeGroupManagementPlugin } from '@ciandtspike.io/plugin-ciandtspike-group-management';
import { ciandtspikeOnboardingTranslationsModule } from '@internal/backstage-plugin-ciandtspike-onboarding-react';
import { entityContentPlugin } from './entityContent/entityContentPlugin';
import { catalogPageModule } from './catalog/catalogPageModule';
import { searchPageModule } from './components/search/searchPageModule';
import { entityFeedbackPlugin as entityFeedbackLegacyPlugin } from '@backstage-community/plugin-entity-feedback';

const entityFeedbackPlugin = convertLegacyPlugin(entityFeedbackLegacyPlugin, {
  extensions: [],
});

// Único uso remanescente de @backstage/core-compat-api no App.tsx: converter os
// route refs de catalog/scaffolder/techdocs/apiDocs/org para o bindRoutes.
//
// NÃO é defasagem de versão — mesmo nas versões latest desses plugins o `/alpha`
// não exporta constantes de route ref (é design do novo Frontend System). Por
// isso o bind programático e type-safe precisa dos refs legados convertidos.
// (O marketplacePlugin, plugin novo, expõe refs nativos e dispensa conversão.)
//
// A alternativa seria mover os bindings para `app.routes.bindings` no app-config
// (string ids), zerando o core-compat-api aqui — mas é um movimento lateral que
// troca type-safety por config stringly-typed. Quando o upstream publicar refs
// nativos, trocar `convertLegacyRouteRefs(catalogPlugin.routes)` por
// `catalogPluginAlpha.routes` e remover este import.
const catalogPluginRoutes = convertLegacyRouteRefs(catalogPlugin.routes);
const catalogPluginExternalRoutes = convertLegacyRouteRefs(
  catalogPlugin.externalRoutes,
);
const scaffolderPluginRoutes = convertLegacyRouteRefs(scaffolderPlugin.routes);
const scaffolderPluginExternalRoutes = convertLegacyRouteRefs(
  scaffolderPlugin.externalRoutes,
);
const techdocsPluginRoutes = convertLegacyRouteRefs(techdocsPlugin.routes);
const apiDocsPluginExternalRoutes = convertLegacyRouteRefs(
  apiDocsPlugin.externalRoutes,
);
const catalogImportPluginRoutes = catalogImportPlugin.routes;
const orgPluginExternalRoutes = convertLegacyRouteRefs(
  orgPlugin.externalRoutes,
);

const app = createApp({
  features: [
    hostPlugin,
    appModuleHostCustomizations,
    homePlugin,
    ciandtspikeMarketplacePlugin,
    ciandtspikeMarketplaceTranslationsModule,
    ciandtspikeGroupManagementPlugin,
    ciandtspikeOnboardingTranslationsModule,
    ciandtspikeDeleteEntityPlugin,
    ciandtspikeDeleteEntityTranslationsModule,
    ciandtspikeSecretsPlugin,
    grafanaObservabilityPlugin,
    crossplaneResourcesPlugin,
    kubernetesPlugin,
    devtoolsPlugin,
    devtoolsCustomizationPlugin,
    catalogImportPlugin,
    apiDocsPluginAlpha,
    userSettingsPlugin,
    // catalogGraphPlugin: removido do features — vem via auto-discovery (app.packages)
    catalogUnprocessedEntitiesPlugin,
    searchPlugin,
    catalogPluginAlpha,
    catalogPageModule,
    searchPageModule,
    scaffolderPluginAlpha,
    orgPluginAlpha,
    techdocsPluginAlpha,
    techDocsReportIssueAddonModule,
    techDocsTextSizeAddonModule,
    techDocsLightBoxAddonModule,
    techDocsExpandableNavigationAddonModule,
    entityContentPlugin,
    entityFeedbackPlugin,
  ],
  bindRoutes({ bind }) {
    bind(catalogPluginExternalRoutes, {
      createComponent: scaffolderPluginRoutes.root,
      viewTechDoc: techdocsPluginRoutes.docRoot,
      createFromTemplate: scaffolderPluginRoutes.selectedTemplate,
    });
    bind(apiDocsPluginExternalRoutes, {
      registerApi: catalogImportPluginRoutes.importPage,
    });
    bind(scaffolderPluginExternalRoutes, {
      registerComponent: catalogImportPluginRoutes.importPage,
      viewTechDoc: techdocsPluginRoutes.docRoot,
    });
    bind(orgPluginExternalRoutes, {
      catalogIndex: catalogPluginRoutes.catalogIndex,
    });
    bind(ciandtspikeMarketplacePlugin.externalRoutes, {
      // Templates compartilham o mesmo Provision Wizard — o label do CTA na
      // ProductDetailsPage troca por item.type, mas a rota é a mesma.
      provision: ciandtspikeMarketplacePlugin.routes.productWizard,
      scaffold: ciandtspikeMarketplacePlugin.routes.productWizard,
      // Coluna Grupo das tabelas de Systems/Domains/Resources em
      // ProvisionedResourcesPage — link pro detalhe do grupo dono do recurso.
      groupDetails: ciandtspikeGroupManagementPlugin.routes.groupDetail,
    });
    // FLWP-69578 (S2.3) — o cartão de titularidade no detalhe do grupo
    // navega pra "My Resources" do marketplace já filtrada por grupo e
    // tipo. Primeiro binding cross-plugin entre dois plugins próprios do
    // Spike (os demais bindings acima ligam plugins do próprio Backstage).
    bind(ciandtspikeGroupManagementPlugin.externalRoutes, {
      provisionedResources: ciandtspikeMarketplacePlugin.routes.myResourcesIndex,
    });
  },
});

export default app.createRoot();
